import { createHash } from 'node:crypto';

import type {
  ChangeId,
  DeviceId,
  FeedId,
  FolderId,
  ItemId,
  UserId,
} from '../types/ids.js';
import {
  DELIMITER,
  KeyFormatError,
  decodeFreeTextSegment,
  decodeSchedBucket,
  decodeSeq,
  decodeTimestamp,
  encodeFreeTextSegment,
  encodeSchedBucket,
  encodeSeq,
  encodeTimestamp,
  expectIdentifier,
  expectLiteral,
  splitExactly,
} from './key-primitives.js';

/**
 * Every key shape in [Data-Model.md §2](../../../Technical/Data-Model.md), in
 * one module (M0.5.2).
 *
 * **Nothing outside this file builds a key from strings.** A lint rule enforces
 * it. The reason is not tidiness: a key assembled by hand somewhere else is a
 * query against a partition that may belong to another tenant, and the failure
 * looks like missing data rather than an error.
 *
 * Every decoder validates the whole shape and throws {@link KeyFormatError}
 * rather than returning a partial result.
 */

export interface TableKey {
  readonly PK: string;
  readonly SK: string;
}

export interface Gsi1Key {
  readonly GSI1PK: string;
  readonly GSI1SK: string;
}

/* ------------------------------------------------------------------ *
 * Prefixes. The only place these literals appear.
 * ------------------------------------------------------------------ */

export const PREFIX = {
  user: 'USER',
  feed: 'FEED',
  feedUrl: 'FEEDURL',
  artifact: 'ARTIFACT',
  sched: 'SCHED',
  cognito: 'COGNITO',
} as const;

export const SK_PREFIX = {
  meta: 'META',
  alias: 'ALIAS',
  profile: 'PROFILE',
  identity: 'IDENTITY',
  item: 'ITEM',
  itemRef: 'ITEMREF',
  itemText: 'ITEMTEXT',
  subscription: 'SUB',
  folder: 'FOLDER',
  readState: 'RS',
  star: 'STAR',
  tag: 'TAG',
  archive: 'ARCH',
  change: 'CHG',
  device: 'DEVICE',
  idempotency: 'IDEM',
  import: 'IMPORT',
  export: 'EXPORT',
  tagSet: 'TAGSET',
} as const;

const join = (...parts: string[]): string => parts.join(DELIMITER);

/* ------------------------------------------------------------------ *
 * Partitions
 * ------------------------------------------------------------------ */

/** `USER#<userId>` — every per-tenant record lives here. */
export const userPartition = (userId: UserId): string => join(PREFIX.user, userId);

/** `FEED#<feedId>` — feed metadata and its items share one partition. */
export const feedPartition = (feedId: FeedId): string => join(PREFIX.feed, feedId);

export function decodeUserPartition(pk: string): UserId {
  const [prefix, userId] = splitExactly(pk, 2, 'User partition key');
  expectLiteral(prefix ?? '', PREFIX.user, 'User partition key');
  return expectIdentifier(userId ?? '', 'userId') as UserId;
}

export function decodeFeedPartition(pk: string): FeedId {
  const [prefix, feedId] = splitExactly(pk, 2, 'Feed partition key');
  expectLiteral(prefix ?? '', PREFIX.feed, 'Feed partition key');
  return expectIdentifier(feedId ?? '', 'feedId') as FeedId;
}

/* ------------------------------------------------------------------ *
 * Shared entities
 * ------------------------------------------------------------------ */

/** Feed metadata — `FEED#<feedId>` / `META`. */
export const feedMetaKey = (feedId: FeedId): TableKey => ({
  PK: feedPartition(feedId),
  SK: SK_PREFIX.meta,
});

export function decodeFeedMetaKey(key: TableKey): { feedId: FeedId } {
  expectLiteral(key.SK, SK_PREFIX.meta, 'Feed metadata sort key');
  return { feedId: decodeFeedPartition(key.PK) };
}

/** Feed item — `FEED#<feedId>` / `ITEM#<publishedAt>#<itemId>`. */
export const feedItemKey = (
  feedId: FeedId,
  publishedAt: Date | string,
  itemId: ItemId,
): TableKey => ({
  PK: feedPartition(feedId),
  SK: join(
    SK_PREFIX.item,
    encodeTimestamp(publishedAt),
    expectIdentifier(itemId, 'itemId'),
  ),
});

/**
 * An item's *position* — the part of its sort key that orders it.
 *
 * `<publishedAt>#<itemId>` and nothing else, which makes it a **total** order:
 * the timestamp is fixed-width (see `encodeTimestamp`) and the itemId is a
 * ULID, so no two items share a position and string comparison is date
 * comparison. The timeline cursor is one of these (M1.10.4), which is why it
 * needs no per-feed positions — a bare timestamp could not separate the
 * fifteen items a feed published in the same second.
 */
export const itemPosition = (publishedAt: Date | string, itemId: ItemId): string =>
  join(encodeTimestamp(publishedAt), expectIdentifier(itemId, 'itemId'));

/** The sort key an item position refers to, for a `<` range condition. */
export const itemSortKeyAt = (position: string): string => join(SK_PREFIX.item, position);

/** `begins_with` prefix for access pattern 4 — every item in one feed. */
export const itemPrefix = `${SK_PREFIX.item}${DELIMITER}`;

/**
 * `FEED#<feedId>` / `ITEMTEXT#<publishedAt>#<itemId>` — an archived item's
 * plain text, for search (M3.3.4, M4.1).
 *
 * Beside the item rather than on it. The timeline, read state and every
 * poll's comparison read whole item records with no projection, and twenty
 * kilobytes of text on each would multiply what all of them read; only
 * search wants it. `ITEMTEXT#` sorts outside both `begins_with(ITEM#)` and
 * the `BETWEEN ITEM# AND ITEM#<position>` ranges those reads use, so none of
 * them can pick it up.
 */
export const itemTextKey = (
  feedId: FeedId,
  publishedAt: Date | string,
  itemId: ItemId,
): TableKey => ({
  PK: feedPartition(feedId),
  SK: join(SK_PREFIX.itemText, itemPosition(publishedAt, itemId)),
});

/** The text record's sort key at an item position, for a range condition. */
export const itemTextSortKeyAt = (position: string): string =>
  join(SK_PREFIX.itemText, position);

export function decodeFeedItemKey(key: TableKey): {
  feedId: FeedId;
  publishedAt: string;
  itemId: ItemId;
} {
  const [prefix, publishedAt, itemId] = splitExactly(key.SK, 3, 'Feed item sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.item, 'Feed item sort key');
  return {
    feedId: decodeFeedPartition(key.PK),
    publishedAt: decodeTimestamp(publishedAt ?? ''),
    itemId: expectIdentifier(itemId ?? '', 'itemId') as ItemId,
  };
}

/**
 * URL alias — `FEEDURL#<sha256(url)>` / `ALIAS`.
 *
 * **No GSI1 attributes**, resolving the redundancy flagged in M0.5.1: the base
 * key already *is* the URL hash, so access pattern 14 is a `GetItem` here,
 * exactly as pattern 16 is for artifacts. Indexing it again would duplicate
 * every alias record for a query the base table already answers.
 */
export const feedUrlAliasKey = (urlSha256: string): TableKey => ({
  PK: join(PREFIX.feedUrl, expectHex(urlSha256, 64, 'URL hash')),
  SK: SK_PREFIX.alias,
});

export function decodeFeedUrlAliasKey(key: TableKey): { urlSha256: string } {
  const [prefix, hash] = splitExactly(key.PK, 2, 'URL alias partition key');
  expectLiteral(prefix ?? '', PREFIX.feedUrl, 'URL alias partition key');
  expectLiteral(key.SK, SK_PREFIX.alias, 'URL alias sort key');
  return { urlSha256: expectHex(hash ?? '', 64, 'URL hash') };
}

/**
 * Item pointer — `FEED#<feedId>` / `ITEMREF#<seq:012d>` (OI-16).
 *
 * The public item id is `<feedId>.<seq>` (D23), which is exactly what read
 * state needs and is *not* enough to read the item's own record, whose sort
 * key carries `publishedAt`. This record closes that gap with a second
 * `GetItem` in a partition the caller already knows, rather than by adding a
 * third segment to the id — an id is a contract, and a segment added later
 * makes every client duplicate the items it already holds.
 *
 * It holds `publishedAt` and `itemId` and nothing else. Deliberately not a
 * projection of the item: items are the highest-volume write in the system,
 * which is why D23 declined a GSI over them.
 */
export const itemRefKey = (feedId: FeedId, seq: number): TableKey => ({
  PK: feedPartition(feedId),
  SK: join(SK_PREFIX.itemRef, encodeSeq(seq)),
});

export function decodeItemRefKey(key: TableKey): { feedId: FeedId; seq: number } {
  const [prefix, seq] = splitExactly(key.SK, 2, 'Item pointer sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.itemRef, 'Item pointer sort key');
  return { feedId: decodeFeedPartition(key.PK), seq: Number(seq) };
}

/** Archive artifact — `ARTIFACT#<contentHash>` / `META`. */
export const artifactKey = (contentHash: string): TableKey => ({
  PK: join(PREFIX.artifact, expectHex(contentHash, 64, 'Content hash')),
  SK: SK_PREFIX.meta,
});

export function decodeArtifactKey(key: TableKey): { contentHash: string } {
  const [prefix, hash] = splitExactly(key.PK, 2, 'Artifact partition key');
  expectLiteral(prefix ?? '', PREFIX.artifact, 'Artifact partition key');
  expectLiteral(key.SK, SK_PREFIX.meta, 'Artifact sort key');
  return { contentHash: expectHex(hash ?? '', 64, 'Content hash') };
}

/* ------------------------------------------------------------------ *
 * Per-tenant entities
 * ------------------------------------------------------------------ */

/** User profile — `USER#<userId>` / `PROFILE`. */
export const userProfileKey = (userId: UserId): TableKey => ({
  PK: userPartition(userId),
  SK: SK_PREFIX.profile,
});

export function decodeUserProfileKey(key: TableKey): { userId: UserId } {
  expectLiteral(key.SK, SK_PREFIX.profile, 'User profile sort key');
  return { userId: decodeUserPartition(key.PK) };
}

/** Subscription — `USER#<userId>` / `SUB#<feedId>`. */
export const subscriptionKey = (userId: UserId, feedId: FeedId): TableKey => ({
  PK: userPartition(userId),
  SK: join(SK_PREFIX.subscription, expectIdentifier(feedId, 'feedId')),
});

export function decodeSubscriptionKey(key: TableKey): {
  userId: UserId;
  feedId: FeedId;
} {
  const [prefix, feedId] = splitExactly(key.SK, 2, 'Subscription sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.subscription, 'Subscription sort key');
  return {
    userId: decodeUserPartition(key.PK),
    feedId: expectIdentifier(feedId ?? '', 'feedId') as FeedId,
  };
}

/** Folder — `USER#<userId>` / `FOLDER#<folderId>`. */
export const folderKey = (userId: UserId, folderId: FolderId): TableKey => ({
  PK: userPartition(userId),
  SK: join(SK_PREFIX.folder, expectIdentifier(folderId, 'folderId')),
});

export function decodeFolderKey(key: TableKey): { userId: UserId; folderId: FolderId } {
  const [prefix, folderId] = splitExactly(key.SK, 2, 'Folder sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.folder, 'Folder sort key');
  return {
    userId: decodeUserPartition(key.PK),
    folderId: expectIdentifier(folderId ?? '', 'folderId') as FolderId,
  };
}

/* ------------------------------------------------------------------ *
 * Import jobs (M2.2.2)
 * ------------------------------------------------------------------ */

/**
 * How many digits an import entry's index is padded to.
 *
 * Four, because a single import is capped at 2,000 feeds
 * (`MAX_FEEDS_PER_IMPORT`) and an unpadded index sorts `10` before `2`. The
 * report is read back as one range query and rendered in file order, so the
 * sort has to be the file's order rather than a lexicographic accident.
 */
const IMPORT_INDEX_DIGITS = 4;

const encodeImportIndex = (index: number): string => {
  if (!Number.isInteger(index) || index < 0 || index > 9999) {
    throw new KeyFormatError('Import entry index must be an integer in 0…9999', {
      index,
    });
  }
  return String(index).padStart(IMPORT_INDEX_DIGITS, '0');
};

/**
 * The prefix both an import's job record and all its entries begin with.
 *
 * One range query returns the job and its whole report, which is what makes
 * `GET /subscriptions/import/{jobId}` a single read.
 */
export const importJobPrefix = (jobId: string): string =>
  join(SK_PREFIX.import, expectIdentifier(jobId, 'jobId'));

/** Import job — `USER#<userId>` / `IMPORT#<jobId>#META`. */
export const importJobKey = (userId: UserId, jobId: string): TableKey => ({
  PK: userPartition(userId),
  SK: join(importJobPrefix(jobId), SK_PREFIX.meta),
});

/** One feed's outcome — `USER#<userId>` / `IMPORT#<jobId>#<index:0000>`. */
export const importEntryKey = (
  userId: UserId,
  jobId: string,
  index: number,
): TableKey => ({
  PK: userPartition(userId),
  SK: join(importJobPrefix(jobId), encodeImportIndex(index)),
});

export function decodeImportEntryKey(key: TableKey): {
  userId: UserId;
  jobId: string;
  index: number;
} {
  const [prefix, jobId, index] = splitExactly(key.SK, 3, 'Import entry sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.import, 'Import entry sort key');
  if (!new RegExp(`^\\d{${String(IMPORT_INDEX_DIGITS)}}$`).test(index ?? '')) {
    throw new KeyFormatError('Import entry index segment must be four digits', {
      value: index,
    });
  }
  return {
    userId: decodeUserPartition(key.PK),
    jobId: expectIdentifier(jobId ?? '', 'jobId'),
    index: Number(index),
  };
}

/**
 * Read-state marker — `USER#<userId>` / `RS#<feedId>#<seq:012d>`.
 *
 * The padding is what makes access pattern 5 — a `between` range over one
 * feed's markers — return the right rows.
 */
export const readStateKey = (userId: UserId, feedId: FeedId, seq: number): TableKey => ({
  PK: userPartition(userId),
  SK: join(SK_PREFIX.readState, expectIdentifier(feedId, 'feedId'), encodeSeq(seq)),
});

export function decodeReadStateKey(key: TableKey): {
  userId: UserId;
  feedId: FeedId;
  seq: number;
} {
  const [prefix, feedId, seq] = splitExactly(key.SK, 3, 'Read-state sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.readState, 'Read-state sort key');
  return {
    userId: decodeUserPartition(key.PK),
    feedId: expectIdentifier(feedId ?? '', 'feedId') as FeedId,
    seq: decodeSeq(seq ?? ''),
  };
}

/** Star — `USER#<userId>` / `STAR#<publishedAt>#<itemId>`. */
export const starKey = (
  userId: UserId,
  publishedAt: Date | string,
  itemId: ItemId,
): TableKey => ({
  PK: userPartition(userId),
  SK: join(
    SK_PREFIX.star,
    encodeTimestamp(publishedAt),
    expectIdentifier(itemId, 'itemId'),
  ),
});

export function decodeStarKey(key: TableKey): {
  userId: UserId;
  publishedAt: string;
  itemId: ItemId;
} {
  const [prefix, publishedAt, itemId] = splitExactly(key.SK, 3, 'Star sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.star, 'Star sort key');
  return {
    userId: decodeUserPartition(key.PK),
    publishedAt: decodeTimestamp(publishedAt ?? ''),
    itemId: expectIdentifier(itemId ?? '', 'itemId') as ItemId,
  };
}

/**
 * Tag membership — `USER#<userId>` / `TAG#<tag>#<publishedAt>#<itemId>`.
 *
 * The tag is the one user-authored segment in the whole model, so it is
 * percent-escaped — see `encodeFreeTextSegment`. Prefix queries must use
 * {@link tagPrefix} rather than building the string, or a tag containing `#`
 * will not match its own records.
 */
export const tagKey = (
  userId: UserId,
  tag: string,
  publishedAt: Date | string,
  itemId: ItemId,
): TableKey => ({
  PK: userPartition(userId),
  SK: join(
    SK_PREFIX.tag,
    encodeFreeTextSegment(tag),
    encodeTimestamp(publishedAt),
    expectIdentifier(itemId, 'itemId'),
  ),
});

/** `begins_with` prefix for access pattern 8 — all items carrying one tag. */
export const tagPrefix = (tag: string): string =>
  `${join(SK_PREFIX.tag, encodeFreeTextSegment(tag))}${DELIMITER}`;

export function decodeTagKey(key: TableKey): {
  userId: UserId;
  tag: string;
  publishedAt: string;
  itemId: ItemId;
} {
  const [prefix, tag, publishedAt, itemId] = splitExactly(key.SK, 4, 'Tag sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.tag, 'Tag sort key');
  return {
    userId: decodeUserPartition(key.PK),
    tag: decodeFreeTextSegment(tag ?? ''),
    publishedAt: decodeTimestamp(publishedAt ?? ''),
    itemId: expectIdentifier(itemId ?? '', 'itemId') as ItemId,
  };
}

/** `begins_with` prefix for access pattern 7 — every starred item. */
export const starPrefix = `${SK_PREFIX.star}${DELIMITER}`;

/** The star sort key at an item position, for a range bound (M4.2.1). */
export const starSortKeyAt = (position: string): string => join(SK_PREFIX.star, position);

/** The tag sort key at an item position, for a range bound (M4.2.2). */
export const tagSortKeyAt = (tag: string, position: string): string =>
  `${tagPrefix(tag)}${position}`;

/** `begins_with` prefix over every tag entry, for listing the tags in use. */
export const allTagsPrefix = `${SK_PREFIX.tag}${DELIMITER}`;

/**
 * One item's tag set — `USER#<userId>` / `TAGSET#<publishedAt>#<itemId>` (M4.2.2).
 *
 * The `TAG#` entries are an index *by tag*; nothing in them can answer "which
 * tags does this item have", which is what replacing an item's tags and
 * showing them on a timeline both need. This record is addressable by item,
 * so a page of fifty items finds all of theirs in one batched read.
 */
export const tagSetKey = (
  userId: UserId,
  publishedAt: Date | string,
  itemId: ItemId,
): TableKey => ({
  PK: userPartition(userId),
  SK: join(
    SK_PREFIX.tagSet,
    encodeTimestamp(publishedAt),
    expectIdentifier(itemId, 'itemId'),
  ),
});

export function decodeTagSetKey(key: TableKey): {
  userId: UserId;
  publishedAt: string;
  itemId: ItemId;
} {
  const [prefix, publishedAt, itemId] = splitExactly(key.SK, 3, 'Tag set sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.tagSet, 'Tag set sort key');
  return {
    userId: decodeUserPartition(key.PK),
    publishedAt: decodeTimestamp(publishedAt ?? ''),
    itemId: expectIdentifier(itemId ?? '', 'itemId') as ItemId,
  };
}

/** Archive reference — `USER#<userId>` / `ARCH#<publishedAt>#<itemId>`. */
export const archiveRefKey = (
  userId: UserId,
  publishedAt: Date | string,
  itemId: ItemId,
): TableKey => ({
  PK: userPartition(userId),
  SK: join(
    SK_PREFIX.archive,
    encodeTimestamp(publishedAt),
    expectIdentifier(itemId, 'itemId'),
  ),
});

export function decodeArchiveRefKey(key: TableKey): {
  userId: UserId;
  publishedAt: string;
  itemId: ItemId;
} {
  const [prefix, publishedAt, itemId] = splitExactly(
    key.SK,
    3,
    'Archive reference sort key',
  );
  expectLiteral(prefix ?? '', SK_PREFIX.archive, 'Archive reference sort key');
  return {
    userId: decodeUserPartition(key.PK),
    publishedAt: decodeTimestamp(publishedAt ?? ''),
    itemId: expectIdentifier(itemId ?? '', 'itemId') as ItemId,
  };
}

/** Export job — `USER#<userId>` / `EXPORT#<jobId>` (M4.6.1). */
export const exportJobKey = (userId: UserId, jobId: string): TableKey => ({
  PK: userPartition(userId),
  SK: join(SK_PREFIX.export, expectIdentifier(jobId, 'jobId')),
});

export function decodeExportJobKey(key: TableKey): { userId: UserId; jobId: string } {
  const [prefix, jobId] = splitExactly(key.SK, 2, 'Export job sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.export, 'Export job sort key');
  return {
    userId: decodeUserPartition(key.PK),
    jobId: expectIdentifier(jobId ?? '', 'jobId'),
  };
}

/** Change-log entry — `USER#<userId>` / `CHG#<ulid>`. */
export const changeLogKey = (userId: UserId, changeId: ChangeId): TableKey => ({
  PK: userPartition(userId),
  SK: join(SK_PREFIX.change, expectIdentifier(changeId, 'changeId')),
});

export function decodeChangeLogKey(key: TableKey): {
  userId: UserId;
  changeId: ChangeId;
} {
  const [prefix, changeId] = splitExactly(key.SK, 2, 'Change-log sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.change, 'Change-log sort key');
  return {
    userId: decodeUserPartition(key.PK),
    changeId: expectIdentifier(changeId ?? '', 'changeId') as ChangeId,
  };
}

/**
 * Idempotency record — `USER#<userId>` / `IDEM#<route>#<sha256(key)>`
 * (M1.12.3).
 *
 * The client's key is **hashed**, not escaped. It is free text from the open
 * internet, so it has no length limit and no character rules, and a sort key
 * has both; hashing gives a fixed-width segment and removes the question of
 * what a key containing a `#` should mean.
 *
 * Scoped by user *and* route, which is the criterion — one is not enough.
 * Without the user, two clients that both send `Idempotency-Key: 1` share a
 * cache entry and one receives the other's response. Without the route, a
 * client that reuses its key across two endpoints gets the first endpoint's
 * answer from the second.
 */
export const idempotencyKey = (
  userId: UserId,
  route: string,
  clientKey: string,
): TableKey => ({
  PK: userPartition(userId),
  SK: join(
    SK_PREFIX.idempotency,
    encodeFreeTextSegment(route),
    createHash('sha256').update(clientKey, 'utf8').digest('hex'),
  ),
});

/** Device — `USER#<userId>` / `DEVICE#<deviceId>`. */
export const deviceKey = (userId: UserId, deviceId: DeviceId): TableKey => ({
  PK: userPartition(userId),
  SK: join(SK_PREFIX.device, expectIdentifier(deviceId, 'deviceId')),
});

export function decodeDeviceKey(key: TableKey): { userId: UserId; deviceId: DeviceId } {
  const [prefix, deviceId] = splitExactly(key.SK, 2, 'Device sort key');
  expectLiteral(prefix ?? '', SK_PREFIX.device, 'Device sort key');
  return {
    userId: decodeUserPartition(key.PK),
    deviceId: expectIdentifier(deviceId ?? '', 'deviceId') as DeviceId,
  };
}

/* ------------------------------------------------------------------ *
 * GSI1 — overloaded (Data-Model.md §2)
 * ------------------------------------------------------------------ */

/** Feed scheduling — `SCHED#<bucket>` / `<nextPollAt>`. */
export const feedScheduleGsi1 = (bucket: number, nextPollAt: Date | string): Gsi1Key => ({
  GSI1PK: join(PREFIX.sched, encodeSchedBucket(bucket)),
  GSI1SK: encodeTimestamp(nextPollAt),
});

export function decodeFeedScheduleGsi1(key: Gsi1Key): {
  bucket: number;
  nextPollAt: string;
} {
  const [prefix, bucket] = splitExactly(key.GSI1PK, 2, 'Schedule index partition key');
  expectLiteral(prefix ?? '', PREFIX.sched, 'Schedule index partition key');
  return {
    bucket: decodeSchedBucket(bucket ?? ''),
    nextPollAt: decodeTimestamp(key.GSI1SK),
  };
}

/** Subscribers of a feed — `FEED#<feedId>` / `USER#<userId>`. */
export const subscriptionGsi1 = (feedId: FeedId, userId: UserId): Gsi1Key => ({
  GSI1PK: feedPartition(feedId),
  GSI1SK: userPartition(userId),
});

export function decodeSubscriptionGsi1(key: Gsi1Key): {
  feedId: FeedId;
  userId: UserId;
} {
  return {
    feedId: decodeFeedPartition(key.GSI1PK),
    userId: decodeUserPartition(key.GSI1SK),
  };
}

/**
 * Identity alias — `COGNITO#<sub>` / `IDENTITY`.
 *
 * **A base-table record, not a GSI entry**, for the same reason the URL alias
 * is: the natural key *is* the partition key, so the lookup is a `GetItem`.
 *
 * More importantly it is the **uniqueness anchor for an identity**. A
 * conditional write against `USER#<userId>` cannot enforce one-profile-per-sub,
 * because two concurrent callers mint two different `userId`s and both
 * conditions pass — leaving one Cognito account resolving to two internal
 * identities, nondeterministically. This record is keyed on the `sub` itself,
 * so `attribute_not_exists(PK)` on it means exactly one caller can win.
 */
export const identityAliasKey = (cognitoSub: string): TableKey => ({
  PK: join(PREFIX.cognito, expectIdentifier(cognitoSub, 'cognitoSub')),
  SK: SK_PREFIX.identity,
});

export function decodeIdentityAliasKey(key: TableKey): { cognitoSub: string } {
  const [prefix, sub] = splitExactly(key.PK, 2, 'Identity alias partition key');
  expectLiteral(prefix ?? '', PREFIX.cognito, 'Identity alias partition key');
  expectLiteral(key.SK, SK_PREFIX.identity, 'Identity alias sort key');
  return { cognitoSub: expectIdentifier(sub ?? '', 'cognitoSub') };
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

function expectHex(value: string, length: number, context: string): string {
  if (!new RegExp(`^[0-9a-f]{${String(length)}}$`).test(value)) {
    throw new KeyFormatError(
      `${context} must be ${String(length)} lowercase hexadecimal characters`,
      { value },
    );
  }
  return value;
}
